import { AgGridReact } from "ag-grid-react";
import { Collapse, Modal, Spin, Tag } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { APIConstants } from "../../../../../../Common/APIConstants";
import { useStoreUpdaterWithAPI } from "../../../../../../Hooks/UseStoreUpdaterWithApi";

const { Panel } = Collapse;

const MeterModal = React.memo(({ 
  isIdleModalOpen, 
  isInactiveModalOpen, 
  setIsIdleModalOpen, 
  setIsInactiveModalOpen, 
  loader,
  modalStatus,
  deviceStatus = [],
  refreshKey = 0
}) => {
  const { makeRequest, loading } = useStoreUpdaterWithAPI();
  const [converterMetersMap, setConverterMetersMap] = useState({});
  const [loadingConverters, setLoadingConverters] = useState({});
  const [activeConverterKey, setActiveConverterKey] = useState(null);

  const mode = useSelector((state) => state.darkMode);
  const { isdarkMode } = mode;
  const color = useSelector((state) => state.ThemeChange);

  const getStatusForAPI = () => {
    if (isIdleModalOpen) return 1;
    if (isInactiveModalOpen) return 0;
    return null;
  };

  const fetchConverterMeters = (converterID, converterName) => {
    if (!converterID) return;
    
    setLoadingConverters(prev => ({ ...prev, [converterID]: true }));
    
    const status = getStatusForAPI();
    
    const now = new Date();
    const fromDate = new Date(now);
    fromDate.setHours(fromDate.getHours() - 1);
    
    const formatDateForAPI = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.000`;
    };
    
    const fromDateStr = formatDateForAPI(fromDate);
    const toDateStr = formatDateForAPI(now);
    
    const sectionID = 1;
    
    const apiUrl = `${APIConstants.GET_METER_POPUP_DETAILS}?SectionID=${sectionID}&ConverterID=${converterID}&Status=${status}&FromDate=${fromDateStr}&ToDate=${toDateStr}`;
        
    makeRequest({
      type: APIConstants.GET,
      url: apiUrl,
    }).then((res) => {
      if (res && res.length > 0) {
        const filteredMeters = res.filter(meter => {
          const meterStatus = meter.meterStatus?.toLowerCase();
          if (isIdleModalOpen) {
            return meterStatus === 'idle';
          } else if (isInactiveModalOpen) {
            return meterStatus === 'off';
          }
          return true;
        });
        
        setConverterMetersMap(prev => ({
          ...prev,
          [converterID]: filteredMeters
        }));
      } else {
        setConverterMetersMap(prev => ({
          ...prev,
          [converterID]: []
        }));
      }
      setLoadingConverters(prev => ({ ...prev, [converterID]: false }));
    }).catch((error) => {
      setConverterMetersMap(prev => ({
        ...prev,
        [converterID]: []
      }));
      setLoadingConverters(prev => ({ ...prev, [converterID]: false }));
    });
  };

  const handleConverterClick = (converterID, converterName) => {
    if (activeConverterKey === converterID) {
      setActiveConverterKey(null);
    } else {
      setActiveConverterKey(converterID);
      if (!converterMetersMap[converterID]) {
        fetchConverterMeters(converterID, converterName);
      }
    }
  };

  const handleModalClose = () => {
    setIsIdleModalOpen(false);
    setIsInactiveModalOpen(false);
    setActiveConverterKey(null);
    setConverterMetersMap({});
    setLoadingConverters({});
  };

  const getStatusTag = (status) => {
    switch (status) {
      case "ON":
        return <Tag color="green">On</Tag>;
      case "idle":
        return <Tag color="orange">Idle</Tag>;
      case "OFF":
        return <Tag color="red">Off</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  const meterColumnDefs = useMemo(
    () => [
      { 
        headerName: "Device ID", 
        field: "meterid", 
        width: 80, 
        flex: 1 
      },
      { 
        headerName: "Device Name", 
        field: "metername", 
        width: 130, 
        flex: 1 
      },
      {
        headerName: "Status",
        field: "meterStatus",
        width: 100,
        flex: 1,
        cellRenderer: ({ value }) => getStatusTag(value),
      },
      {
        headerName: "VLL",
        field: "vllAvg",
        width: 110,
        flex: 1,
      },
      {
        headerName: "I Avg",
        field: "iavg",
        width: 110,
        flex: 1,
      },
      {
        headerName: "kWT",
        field: "kwt",
        width: 110,
        flex: 1,
      },
      {
        headerName: "Last Comm.",
        field: "createdon",
        flex: 1,
        minWidth: 220,
        valueFormatter: (params) => {
          return params.value ? params.value.replace("T", " ") : "";
        },
      },
    ],
    []
  );

  const isModalOpen = isIdleModalOpen || isInactiveModalOpen;
  const isEmpty = !deviceStatus || deviceStatus.length === 0;
  const isLoading = loader;

  useEffect(() => {
    if (isModalOpen) {
      setConverterMetersMap({});
      setLoadingConverters({});
      setActiveConverterKey(null);
    }
  }, [isModalOpen, refreshKey]);

  return (
    <Modal
      open={isModalOpen}
      onCancel={handleModalClose}
      closable={false}
      style={{ top: 80 }}
      bodyStyle={{ 
        padding: 0, 
        height: "calc(80vh)", 
        display: "flex", 
        flexDirection: "column" 
      }}
      className={`no-padding-modal ${isdarkMode ? "dark-modal" : ""}`}
      footer={null}
      width="60%"
    >
      {/* Header */}
      <div
        style={{
          padding: "10px 16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: color.themeConfig.colorPrimary,
          borderRadius: "6px 6px 0 0",
          flexShrink: 0,
        }}
      >
        <span style={{ color: "white", fontSize: 16, fontWeight: 500 }}>
          {isIdleModalOpen ? "IDLE CONVERTERS LIST" : "INACTIVE CONVERTERS LIST"}
        </span>
        <span
          style={{
            color: "white",
            fontSize: 18,
            cursor: "pointer",
          }}
          onClick={handleModalClose}
        >
          ✕
        </span>
      </div>

      {/* Scrollable content */}
      <div style={{ overflowY: "auto", flex: 1, padding: "16px" }}>
        {isLoading ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <Spin size="large" />
          </div>
        ) : isEmpty ? (
          <div
            style={{
              textAlign: "center",
              padding: "20px",
              color: isdarkMode ? "#ccc" : "#666",
              fontSize: "16px",
            }}
          >
            No converters available
          </div>
        ) : (
          <Collapse
            accordion
            activeKey={activeConverterKey}
            onChange={(key) => {
              const converter = deviceStatus.find(c => c.converterID == key[0]);
              if (converter) {
                handleConverterClick(converter.converterID, converter.converterName);
              }
            }}
           className={`border-l-red-500 border-l-4 border-y-0 rounded-md  custom-collapse`}
                bodyStyle={{ padding: 0 }}
          >
            {deviceStatus.map((converter) => (
              <Panel
                key={converter.converterID}
                className={`rounded-t-xl ${isdarkMode ? "dark-collapse" : "light-collapse"}`}
                header={
                  <div className="flex justify-between items-center w-full">
                    <span className="font-semibold">{converter.converterName}</span>
                    <div className="flex gap-2">
                      <Tag color="green">Active: {converter.activeDevice || 0}</Tag>
                      <Tag color="red">Inactive: {converter.inActiveDevice || 0}</Tag>
                      {converter.idleDevice !== undefined && (
                        <Tag color="orange">Idle: {converter.idleDevice || 0}</Tag>
                      )}
                    </div>
                  </div>
                }
                bodyStyle={{ padding: "12px" }}
              >
                {activeConverterKey === converter.converterID && (
                  <div
                    className="custom-grid-wrapper"
                    style={{
                      "--ag-header-background-color": color.themeConfig.colorPrimary,
                      "--ag-header-foreground-color": "#fff",
                      "--ag-header-group-bg": color.themeConfig.colorPrimaryShade,
                      width: "100%",
                    }}
                  >
                    {loadingConverters[converter.converterID] ? (
                      <div style={{ textAlign: "center", padding: "20px" }}>
                        <Spin />
                      </div>
                    ) : converterMetersMap[converter.converterID]?.length === 0 ? (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "20px",
                          color: isdarkMode ? "#ccc" : "#666",
                        }}
                      >
                        No {isIdleModalOpen ? "idle" : "inactive"} meters found
                      </div>
                    ) : (
                      <AgGridReact
                        suppressRowTransform={true}
                        rowData={converterMetersMap[converter.converterID] || []}
                        domLayout="autoHeight"
                        columnDefs={meterColumnDefs}
                      />
                    )}
                  </div>
                )}
              </Panel>
            ))}
          </Collapse>
        )}
      </div>
    </Modal>
  );
});

export default MeterModal;